<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- FileUpload Component -->
      <div class="p-input-wrapper">
        <FileUpload ref="fileUploadRef" :id="id" v-model="localValue" :name="name" :url="url" :mode="mode"
          :multiple="multiple" :accept="accept" :disabled="disabled" :auto="auto" :maxFileSize="maxFileSize"
          :invalidFileSizeMessage="invalidFileSizeMessage" :invalidFileTypeMessage="invalidFileTypeMessage"
          :fileLimit="fileLimit" :invalidFileLimitMessage="invalidFileLimitMessage" :withCredentials="withCredentials"
          :previewWidth="previewWidth" :chooseLabel="chooseLabel" :uploadLabel="uploadLabel" :cancelLabel="cancelLabel"
          :customUpload="customUpload" :showUploadButton="showUploadButton" :showCancelButton="showCancelButton"
          :chooseIcon="chooseIcon" :uploadIcon="uploadIcon" :cancelIcon="cancelIcon" :style="style" :class="[
            props.class,
            {
              'p-invalid': isInvalid,
              'p-disabled': disabled
            }
          ]" :aria-label="label" :aria-invalid="isInvalid" :aria-required="required"
          :aria-describedby="ariaDescribedBy" :required="required" v-bind="$attrs" @upload="onUpload"
          @uploader="onCustomUpload" @select="onSelect" @error="onError" @remove="onRemove" @clear="onClear"
          @before-upload="onBeforeUpload" @progress="onProgress">
          <template #empty v-if="$slots.empty">
            <slot name="empty" />
          </template>
          <template #content="slotProps" v-if="$slots.content">
            <slot name="content" :files="slotProps.files" />
          </template>
        </FileUpload>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-for="(error, index) in localizedErrors" :key="index" :id="`${id}-error-${index}`" class="p-error block">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import type { ONEFileUploadProps, FileUploadEmits, UploadFile } from './types'
import type { FileUploadUploaderEvent, FileUploadSelectEvent, FileUploadProgressEvent, FileUploadRemoveEvent } from 'primevue/fileupload'

export default defineComponent({
  name: 'ONEFileUpload',
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import FileUpload from 'primevue/fileupload'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'

const props = withDefaults(defineProps<ONEFileUploadProps>(), {
  modelValue: null,
  mode: 'advanced',
  name: undefined,
  url: undefined,
  multiple: false,
  accept: undefined,
  disabled: false,
  auto: false,
  maxFileSize: undefined,
  invalidFileSizeMessage: undefined,
  invalidFileTypeMessage: undefined,
  fileLimit: undefined,
  invalidFileLimitMessage: undefined,
  withCredentials: false,
  previewWidth: 50,
  chooseLabel: undefined,
  uploadLabel: undefined,
  cancelLabel: undefined,
  customUpload: false,
  showUploadButton: true,
  showCancelButton: true,
  chooseIcon: 'pi pi-plus',
  uploadIcon: 'pi pi-upload',
  cancelIcon: 'pi pi-times',
  style: undefined,
  class: undefined,
  label: '',
  required: false,
  helperText: '',
  id: () => `one-fileupload-${Math.random().toString(36).substr(2, 9)}`,
  invalid: false,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  formContext: true
})

const emit = defineEmits<FileUploadEmits>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const fileUploadRef = ref<any>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const localValue = computed({
  get: () => props.modelValue ?? null,
  set: (newValue) => {
    emit('update:modelValue', newValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, newValue)
    }
  }
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onUpload = (event: any) => {
  isDirty.value = true
  emit('upload', event)
  if (props.validateOnChange) {
    validate()
  }
}

const onCustomUpload = (event: FileUploadUploaderEvent) => {
  emit('uploader', event)
}

const onSelect = (event: FileUploadSelectEvent) => {
  isDirty.value = true
  emit('select', event)
  if (props.validateOnInput) {
    validate()
  }
}

const onError = (event: any) => {
  emit('error', event)
}

const onRemove = (event: FileUploadRemoveEvent) => {
  emit('remove', event)
}

const onClear = () => {
  isDirty.value = false
  localValidationErrors.value = []
  emit('clear')
}

const onBeforeUpload = (event: any) => {
  emit('before-upload', event)
}

const onProgress = (event: FileUploadProgressEvent) => {
  emit('progress', event)
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

const isValid = computed(() => currentErrors.value.length === 0)

defineExpose({
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid,
  clear: onClear
})
</script>
